import { useMutation, useQuery } from 'react-query';
import { request, requestXlsxFileDownload } from '../utils/axios-utils';
import { resolvePhotoShootSessionDateQuery } from '../utils/photoShootSessionDate';

const buildSessionDateQuery = date => `date=${encodeURIComponent(resolvePhotoShootSessionDateQuery(date))}`;

export const useGetStats = (date, autoReload = false) =>
    useQuery(['shootSessionStats', date ?? 'current'], () => GetStats(date), {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: autoReload,
        refetchInterval: autoReload ? 30000 : false,
    });

export const useGetMergedLinkForShootPending = () =>
    useMutation({
        mutationFn: async ([date]) => {
            return await GetMergedLinkForShootPending(date);
        },
    });

const getShotSkusByCompany = async (date, companyCode, sessionId) => {
    const query = new URLSearchParams();
    query.set('date', resolvePhotoShootSessionDateQuery(date));
    query.set('companyCode', companyCode);
    if (sessionId) query.set('sessionId', sessionId);
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/shotSkusByCompany?${query}`,
    });
    return data;
};

export const useGetShotSkusByCompany = () =>
    useMutation({
        mutationFn: async ([date, companyCode, sessionId]) => getShotSkusByCompany(date, companyCode, sessionId),
    });

const getOpenReshootSkus = async date => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/openReshootSkus?${buildSessionDateQuery(date)}`,
    });
    return data;
};

export const useGetOpenReshootSkus = () =>
    useMutation({
        mutationFn: async ([date]) => getOpenReshootSkus(date),
    });

const getGuidelineAdherenceSkus = async sessionId => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/guidelineAdherenceSkus?sessionId=${encodeURIComponent(sessionId)}`,
    });
    return data;
};

export const useGetGuidelineAdherenceSkus = () =>
    useMutation({
        mutationFn: async ([sessionId]) => getGuidelineAdherenceSkus(sessionId),
    });

const GetStats = async date => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/GetStats?${buildSessionDateQuery(date)}`,
    });
    return data;
};

const GetMergedLinkForShootPending = async date => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/GetMergedLinkForShootPending?${buildSessionDateQuery(date)}`,
    });
    return data;
};

const GetDownloadXLSheetReport = async date => {
    return await requestXlsxFileDownload({
        method: 'GET',
        url: `/photoshootsession/downloadStats?${buildSessionDateQuery(date)}`,
    });
};

export const useGetDownloadXLSheetReport = () =>
    useMutation({
        mutationFn: async ([date]) => {
            return await GetDownloadXLSheetReport(date);
        },
    });

const GetTeamDetailsOfShoot = async sessionId => {
    const { data } = await request({
        method: 'get',
        url: `/photoshootsession/getSessionById/${sessionId}`,
    });
    return data;
};

const GetBayTeams = async date => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/bayTeams?${buildSessionDateQuery(date)}`,
    });
    return data;
};

const GetBayPerformance = async date => {
    const { data } = await request({
        method: 'GET',
        url: `/photoshootsession/bayPerformance?${buildSessionDateQuery(date)}`,
    });
    return data;
};

export const useGetBayPerformance = (date, autoReload = false) =>
    useQuery(['bayPerformance', date ?? 'current'], () => GetBayPerformance(date), {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: autoReload,
        refetchInterval: autoReload ? 30000 : false,
    });

export const useGetBayTeams = () =>
    useMutation({
        mutationFn: async ([date]) => {
            return await GetBayTeams(date);
        },
    });

export const useGetTeamDetailsOfShoot = () =>
    useMutation({
        mutationFn: async ([sessionId]) => {
            return await GetTeamDetailsOfShoot(sessionId);
        },
    });
const getSkuSessionInfo = async sessionId => {
    const { data } = await request({
        method: 'get',
        url: `/photoshootsession/sessionSkuInfo?sessionId=${sessionId}`,
    });
    return data;
};
export const useGetSkuSessionInfo = () =>
    useMutation({
        mutationFn: async ([sessionId]) => {
            return await getSkuSessionInfo(sessionId);
        },
    });

const getSessionShootComplete = async sessionId => {
    const { data } = await request({
        method: 'get',
        url: `/photoshootsession/sessionShootComplete?sessionId=${sessionId}`,
    });
    return data;
};

export const useGetSessionShootComplete = () =>
    useMutation({
        mutationFn: async ([sessionId]) => {
            return await getSessionShootComplete(sessionId);
        },
    });
